import { Config } from '@/core/types'
import KeystoneService from './keystoneService'
import resolveDomainLocale, {
  type DomainConfigData,
  clearResolutionCache,
} from '@/core/utils/locale/resolveDomainLocale'

// Import fallback configuration
import {
  config as fallbackConfig,
  domains as fallbackDomains,
} from '../../../config/fallback'

interface AppConfigOptions {
  keystoneBaseUrl: string
  keystoneConfigKey: string
  keystoneApiKey?: string
  enableAutoUpdate?: boolean
  updateCheckInterval?: number // default: 30 min
  idleThreshold?: number // default: 5 min
  keystoneTimeout?: number
  keystoneRetryCount?: number
  keystoneRetryDelay?: number
}

interface RequiredAppConfigOptions extends Omit<AppConfigOptions, 'keystoneApiKey'> {
  keystoneApiKey?: string // Keep as optional
}

type ConfigSource = 'keystone' | 'fallback'

interface ConfigState {
  config: Config
  domains: Record<string, string>
  version: string
  lastUpdated: Date
  source: ConfigSource
}

const MAX_FAILURES_BEFORE_BACKOFF = 5
const MAX_BACKOFF_MULTIPLIER = 8 // 8x base interval = ~4h at 30min base
const FORCED_UPDATE_INTERVAL = 2 * 60 * 60 * 1000 // 2 hours
const MIN_COUNTRY_RATIO = 0.5 // reject config with >50% country drop

class AppConfigService {
  private static instance: AppConfigService | null = null
  private keystoneService: KeystoneService | null = null
  private currentState: ConfigState
  private options: RequiredAppConfigOptions
  private updateTimeout: ReturnType<typeof setTimeout> | null = null
  private isInitialized = false
  private updateInProgress = false
  private consecutiveFailures = 0
  private shutdownHandlers: (() => void)[] = []

  // Cached config data object — invalidated only when currentState changes
  private cachedConfigData: DomainConfigData | null = null

  // Activity tracking for intelligent updates
  private activityTracker = {
    lastActivity: Date.now(),
    requestCount: 0,
  }

  private constructor() {
    this.currentState = {
      config: fallbackConfig,
      domains: fallbackDomains,
      version: 'fallback-initial',
      lastUpdated: new Date(),
      source: 'fallback',
    }

    this.options = {} as RequiredAppConfigOptions
  }

  static getInstance(): AppConfigService {
    if (!AppConfigService.instance) {
      AppConfigService.instance = new AppConfigService()
    }
    return AppConfigService.instance
  }

  private static shouldInitialize(): boolean {
    return (
      typeof window === 'undefined' &&
      process.env.NODE_ENV !== 'test' &&
      process.env.NEXT_PHASE !== 'phase-production-build'
    )
  }

  async initialize(options: AppConfigOptions): Promise<ConfigState> {
    if (this.isInitialized) {
      console.info('AppConfigService already initialized')
      return this.currentState
    }

    if (!AppConfigService.shouldInitialize()) {
      console.info(
        'Skipping config service initialization (build time/test/client-side)'
      )
      return this.currentState
    }

    console.info('Initializing AppConfigService...', {
      keystoneUrl: options.keystoneBaseUrl,
      enableAutoUpdate: options.enableAutoUpdate ?? true,
    })

    this.options = {
      enableAutoUpdate: true,
      updateCheckInterval: 30 * 60 * 1000,
      idleThreshold: 5 * 60 * 1000,
      keystoneTimeout: 15000,
      keystoneRetryCount: 3,
      keystoneRetryDelay: 2000,
      ...options,
    }

    try {
      this.keystoneService = new KeystoneService({
        baseUrl: this.options.keystoneBaseUrl,
        configKey: this.options.keystoneConfigKey,
        apiKey: this.options.keystoneApiKey,
        timeout: this.options.keystoneTimeout,
        retryCount: this.options.keystoneRetryCount,
        retryDelay: this.options.keystoneRetryDelay,
      })

      await this.loadConfiguration()

      if (this.options.enableAutoUpdate) {
        this.scheduleNextUpdate()
      }

      this.isInitialized = true
      this.registerShutdownHandlers()

      console.info('AppConfigService initialized successfully', {
        version: this.currentState.version,
        source: this.currentState.source,
        countries: Object.keys(this.currentState.config).length,
        domains: Object.keys(this.currentState.domains).length,
      })

      return this.currentState
    } catch (error) {
      console.error(
        'AppConfigService initialization failed, using build fallback:',
        error
      )

      this.isInitialized = true
      this.registerShutdownHandlers()

      return this.currentState
    }
  }

  /**
   * Load configuration from Keystone, preserving in-memory state on failure.
   * After the first successful Keystone load, the build fallback is never used again —
   * failures keep the last known good Keystone data.
   */
  private async loadConfiguration(): Promise<void> {
    if (!this.keystoneService) {
      throw new Error('Keystone service not initialized')
    }

    try {
      const keystoneData = await this.keystoneService.fetchConfigurations()
      const { config, domains } =
        this.keystoneService.transformToAppConfig(keystoneData)

      // if (!this.isConfigSafe(config, domains)) {
      //   console.error('Config safety check failed, keeping current config', {
      //     currentCountries: Object.keys(this.currentState.config).length,
      //     newCountries: Object.keys(config).length,
      //     currentVersion: this.currentState.version,
      //     newVersion: keystoneData.version
      //   })
      //   return
      // }

      const previousVersion = this.currentState.version
      const previousSource = this.currentState.source

      this.currentState = {
        config,
        domains,
        version: keystoneData.version,
        lastUpdated: new Date(),
        source: 'keystone',
      }

      this.invalidateConfigDataCache()
      clearResolutionCache()
      this.consecutiveFailures = 0

      console.info('Configuration loaded from Keystone', {
        version: this.currentState.version,
        previousVersion,
        previousSource,
        countries: Object.keys(config).length,
      })
    } catch (error) {
      this.consecutiveFailures++

      if (this.currentState.source === 'keystone') {
        console.warn(
          'Failed to update from Keystone, keeping current in-memory config:',
          {
            error: (error as Error).message,
            currentVersion: this.currentState.version,
            lastUpdated: this.currentState.lastUpdated,
            consecutiveFailures: this.consecutiveFailures,
          }
        )
      } else {
        console.warn('Failed to load from Keystone, using build fallback:', {
          error: (error as Error).message,
          consecutiveFailures: this.consecutiveFailures,
        })
      }
    }
  }

  /**
   * Validate that new configuration is not drastically degraded compared to current.
   * Prevents corrupted/partial Keystone responses from replacing a working config.
   */
  private isConfigSafe(newConfig: Config, newDomains: Record<string, string>): boolean {
    const currentCountries = Object.keys(this.currentState.config).length
    const newCountries = Object.keys(newConfig).length

    if (currentCountries > 0 && newCountries < currentCountries * MIN_COUNTRY_RATIO) {
      return false
    }

    if (newCountries === 0 && currentCountries > 0) {
      return false
    }

    const newDomainCount = Object.keys(newDomains).length
    return !(newDomainCount === 0 && Object.keys(this.currentState.domains).length > 0)
  }

  /**
   * Schedule the next auto-update check using a setTimeout chain.
   * Each iteration waits for the previous to complete before scheduling the next,
   * preventing overlapping async executions that setInterval can cause.
   */
  private scheduleNextUpdate(): void {
    if (!this.options.enableAutoUpdate) return

    this.updateTimeout = setTimeout(async () => {
      await this.autoUpdateTick()
      this.scheduleNextUpdate()
    }, this.options.updateCheckInterval)
  }

  /**
   * Single tick of the auto-update loop.
   * Checks circuit breaker, idle state, and forced interval before attempting update.
   */
  private async autoUpdateTick(): Promise<void> {
    if (this.updateInProgress) return

    // Circuit breaker: exponential backoff on consecutive failures
    if (this.consecutiveFailures >= MAX_FAILURES_BEFORE_BACKOFF) {
      const backoffMultiplier = Math.min(
        Math.pow(2, this.consecutiveFailures - MAX_FAILURES_BEFORE_BACKOFF),
        MAX_BACKOFF_MULTIPLIER
      )
      const timeSinceLastUpdate = Date.now() - this.currentState.lastUpdated.getTime()
      const backoffInterval = this.options.updateCheckInterval * backoffMultiplier

      if (timeSinceLastUpdate < backoffInterval) {
        console.info('Auto-update skipped (circuit breaker backoff)', {
          consecutiveFailures: this.consecutiveFailures,
          backoffMultiplier,
          nextCheckIn: backoffInterval - timeSinceLastUpdate,
        })
        return
      }
    }

    const timeSinceLastActivity = Date.now() - this.activityTracker.lastActivity
    const isIdle = timeSinceLastActivity > this.options.idleThreshold
    const timeSinceLastUpdate = Date.now() - this.currentState.lastUpdated.getTime()
    const shouldUpdate = isIdle || timeSinceLastUpdate > FORCED_UPDATE_INTERVAL

    if (shouldUpdate) {
      try {
        await this.updateConfiguration()
      } catch (error) {
        console.warn('Auto-update failed:', {
          error: (error as Error).message,
          consecutiveFailures: this.consecutiveFailures,
        })
      }
    }
  }

  /**
   * Update configuration from Keystone.
   * Clears KeystoneService cache before fetching to avoid stale cached responses
   * after a version change has been confirmed by the lightweight meta check.
   */
  private async updateConfiguration(): Promise<void> {
    if (!this.keystoneService || this.updateInProgress) {
      return
    }

    this.updateInProgress = true

    try {
      const updateCheck = await this.keystoneService.checkForUpdates()
      if (!updateCheck.hasUpdates) {
        this.consecutiveFailures = 0
        return
      }

      console.info('Configuration update detected, fetching...', {
        newVersion: updateCheck.version,
        currentVersion: this.currentState.version,
      })

      this.keystoneService.clearCache()
      await this.loadConfiguration()

      if (this.currentState.source === 'keystone') {
        console.info('Configuration updated successfully', {
          version: this.currentState.version,
          source: this.currentState.source,
        })
      }
    } finally {
      this.updateInProgress = false
    }
  }

  /**
   * Register process signal handlers for graceful shutdown
   */
  private registerShutdownHandlers(): void {
    const handler = () => this.shutdown()

    process.once('SIGTERM', handler)
    process.once('SIGINT', handler)
    this.shutdownHandlers = [handler]
  }

  /**
   * Invalidate cached DomainConfigData so the next getDomainLocale call rebuilds it.
   * Called whenever currentState changes.
   */
  private invalidateConfigDataCache(): void {
    this.cachedConfigData = null
  }

  /**
   * Get or build the DomainConfigData object for locale resolution.
   * Cached to avoid creating a new object on every SSR request — the config
   * only changes on update (every 30+ min), so this is a cache hit 99.99% of the time.
   */
  private getConfigData(): DomainConfigData {
    if (!this.cachedConfigData) {
      this.cachedConfigData = {
        domains: this.currentState.domains,
        config: this.currentState.config,
        version: this.currentState.version,
      }
    }
    return this.cachedConfigData
  }

  registerActivity(): void {
    this.activityTracker.lastActivity = Date.now()
    this.activityTracker.requestCount++
  }

  getConfig(): ConfigState {
    return { ...this.currentState }
  }

  getCountryConfig(country: string): Config[string] | null {
    return this.currentState.config[country] || null
  }

  getDomains(): Record<string, string> {
    return { ...this.currentState.domains }
  }

  getDomainLocale = (context: any) => {
    return resolveDomainLocale(context, this.getConfigData())
  }

  getHealthStatus() {
    return {
      isInitialized: this.isInitialized,
      version: this.currentState.version,
      source: this.currentState.source,
      lastUpdated: this.currentState.lastUpdated,
      updateInProgress: this.updateInProgress,
      activityCount: this.activityTracker.requestCount,
      consecutiveFailures: this.consecutiveFailures,
    }
  }

  shutdown(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = null
    }

    this.shutdownHandlers.forEach((handler) => {
      process.removeListener('SIGTERM', handler)
      process.removeListener('SIGINT', handler)
    })
    this.shutdownHandlers = []

    this.isInitialized = false
    console.info('AppConfigService shut down')
  }

  /**
   * Reset singleton for testing purposes
   */
  static resetInstance(): void {
    if (AppConfigService.instance) {
      AppConfigService.instance.shutdown()
      AppConfigService.instance = null
    }
  }
}

export default AppConfigService
export type { AppConfigOptions, ConfigState }
